import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Truck, X, Search, Loader2, Plus, Sparkles, Lock, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import { suppliersApi } from '../lib/suppliersApi';
import { supabase } from '../lib/supabaseClient';

// Offline cache helpers (shared keys with Suppliers.jsx + SupplierDetail.jsx).
const suppliersListCacheKey = (uid) => `sp.suppliers.list.cache:${uid || 'anon'}`;
const supplierDetailCacheKey = (id) => `sp.supplier.cache:${id}`;

function readListCache(uid) {
  try {
    const raw = localStorage.getItem(suppliersListCacheKey(uid));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function readPricesCache(supplierId) {
  try {
    const raw = localStorage.getItem(supplierDetailCacheKey(supplierId));
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed?.prices || null;
  } catch { return null; }
}

// Picker that lets the user add line items to a quote from one of their
// supplier price lists. Designed to behave like the existing CatalogTools
// picker so the rest of NewSubmission.jsx stays untouched.
//
// Props:
//   onPick({ name, unit, price })  // same signature as catalog onPick
//   lang                            // 'fr' | 'en'
export default function SupplierPicker({ onPick, lang = 'fr' }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paywall, setPaywall] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [prices, setPrices] = useState([]);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [search, setSearch] = useState('');
  const [isOffline, setIsOffline] = useState(false);
  const [uid, setUid] = useState(null);

  // Resolve current user id once — used to scope the localStorage cache.
  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) setUid(data?.session?.user?.id || null);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Load suppliers on first open
  useEffect(() => {
    if (!open || suppliers.length || paywall) return;
    let cancelled = false;
    const fetchSuppliers = async () => {
      setLoading(true);
      // 🌐 Offline: lire le cache directement, aucun appel réseau.
      if (!navigator.onLine) {
        const cached = readListCache(uid);
        if (cached && !cancelled) {
          setSuppliers(cached);
          if (cached.length === 1) setSelectedId(cached[0].id);
        }
        if (!cancelled) {
          setIsOffline(true);
          setLoading(false);
        }
        return;
      }
      try {
        const data = await suppliersApi.list();
        if (cancelled) return;
        setSuppliers(data.suppliers || []);
        setIsOffline(false);
        if ((data.suppliers || []).length === 1) {
          setSelectedId(data.suppliers[0].id);
        }
      } catch (err) {
        if (cancelled) return;
        if (err.status === 402) {
          setPaywall(true);
        } else {
          // Fallback cache au lieu de toast d'erreur (UX offline).
          const cached = readListCache(uid);
          if (cached) {
            setSuppliers(cached);
            setIsOffline(true);
            if (cached.length === 1) setSelectedId(cached[0].id);
          } else {
            toast.error(err.message || 'Erreur de chargement');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchSuppliers();
    return () => {
      cancelled = true;
    };
  }, [open, suppliers.length, paywall, uid]);

  // Load prices when supplier selected
  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    const fetchPrices = async () => {
      setLoadingPrices(true);
      // 🌐 Offline: lire le cache directement, aucun toast d'erreur.
      if (!navigator.onLine) {
        const cached = readPricesCache(selectedId);
        if (!cancelled) {
          setPrices(cached || []);
          setIsOffline(true);
          setLoadingPrices(false);
        }
        return;
      }
      try {
        const data = await suppliersApi.listPrices(selectedId);
        if (!cancelled) {
          setPrices(data.prices || []);
          setIsOffline(false);
        }
      } catch (err) {
        if (cancelled) return;
        // Fallback cache au lieu de toast d'erreur.
        const cached = readPricesCache(selectedId);
        if (cached) {
          setPrices(cached);
          setIsOffline(true);
        } else {
          toast.error(err.message || 'Erreur de chargement des prix');
        }
      } finally {
        if (!cancelled) setLoadingPrices(false);
      }
    };
    fetchPrices();
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const filtered = useMemo(() => {
    if (!search.trim()) return prices;
    const q = search.toLowerCase();
    return prices.filter(
      (p) =>
        (p.description || '').toLowerCase().includes(q) ||
        (p.sku || '').toLowerCase().includes(q)
    );
  }, [search, prices]);

  const close = () => {
    setOpen(false);
    setSearch('');
  };

  const pickPrice = (p) => {
    onPick({
      name: p.description,
      unit: p.unit || 'un',
      price: Number(p.unit_price) || 0,
    });
    toast.success('+ ' + p.description, { duration: 1500 });
    close();
  };

  return (
    <>
      <button
        data-testid="open-supplier-picker"
        onClick={() => setOpen(true)}
        className="btn-outline gap-2 !min-h-[48px] text-sm relative"
        style={{ borderColor: '#C9A961', color: '#5A1620' }}
      >
        <Truck size={16} />
        {lang === 'fr' ? 'Mes prix fournisseurs' : 'My supplier prices'}
        <span
          className="absolute -top-1.5 -right-1.5 text-[8px] font-bold px-1.5 py-0.5 rounded-full"
          style={{ backgroundColor: '#C9A961', color: '#5A1620' }}
        >
          NEW
        </span>
      </button>

      {open && (
        <div
          data-testid="supplier-picker-modal"
          className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={close}
        >
          <div
            className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-4 border-b border-[#E5DCC9] flex items-center justify-between gap-2 sticky top-0 bg-white">
              <div className="min-w-0">
                <div className="font-heading font-black text-xl truncate">
                  {lang === 'fr' ? 'Mes prix fournisseurs' : 'My supplier prices'}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {selectedId
                    ? suppliers.find((s) => s.id === selectedId)?.name || ''
                    : lang === 'fr'
                    ? 'Choisis un fournisseur'
                    : 'Pick a supplier'}
                </div>
              </div>
              <button
                data-testid="close-supplier-picker"
                onClick={close}
                className="h-10 w-10 flex items-center justify-center rounded-lg hover:bg-gray-100 shrink-0"
              >
                <X size={18} />
              </button>
            </div>

            {/* Offline banner */}
            {isOffline && (
              <div
                className="px-4 py-2 flex items-center gap-2 text-xs"
                style={{ backgroundColor: '#FEF3C7', color: '#92400E', borderBottom: '1px solid #FCD34D' }}
                data-testid="picker-offline-banner"
              >
                <WifiOff className="w-3.5 h-3.5 flex-shrink-0" />
                <span>
                  {lang === 'fr'
                    ? 'Hors ligne — données en cache.'
                    : 'Offline — showing cached data.'}
                </span>
              </div>
            )}

            {/* Body */}
            {paywall ? (
              <div className="p-6 text-center" data-testid="picker-paywall">
                <div
                  className="w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center"
                  style={{ backgroundColor: '#7A1F2B', color: '#C9A961' }}
                >
                  <Lock className="w-7 h-7" />
                </div>
                <div
                  className="inline-flex items-center gap-1 text-[10px] font-bold tracking-widest mb-2"
                  style={{ color: '#C9A961' }}
                >
                  <Sparkles className="w-3 h-3" /> FONCTIONNALITÉ PRO
                </div>
                <h3 className="font-heading text-lg font-black mb-2" style={{ color: '#5A1620' }}>
                  Réservé aux abonnés payants
                </h3>
                <p className="text-sm mb-4" style={{ color: '#4A5568' }}>
                  {lang === 'fr'
                    ? "L'essai gratuit de 3 jours n'inclut pas cette fonctionnalité. Souscris pour utiliser tes prix fournisseurs dans tes soumissions."
                    : "The 3-day free trial doesn't include this feature. Subscribe to use your supplier prices."}
                </p>
                <Link
                  to="/pricing"
                  onClick={close}
                  className="inline-block w-full py-3 rounded-xl font-semibold text-white text-base"
                  style={{ backgroundColor: '#7A1F2B' }}
                  data-testid="picker-upgrade-btn"
                >
                  {lang === 'fr' ? 'Voir les plans' : 'See plans'}
                </Link>
              </div>
            ) : loading ? (
              <div className="p-8 flex items-center justify-center">
                <Loader2 className="w-7 h-7 animate-spin" style={{ color: '#7A1F2B' }} />
              </div>
            ) : suppliers.length === 0 ? (
              <div className="p-6 text-center" data-testid="picker-no-suppliers">
                <Truck className="w-10 h-10 mx-auto mb-3" style={{ color: '#C9A961' }} />
                <h3 className="font-bold mb-1" style={{ color: '#5A1620' }}>
                  {lang === 'fr' ? 'Aucun fournisseur' : 'No suppliers yet'}
                </h3>
                <p className="text-sm mb-4" style={{ color: '#6B7280' }}>
                  {lang === 'fr'
                    ? "Ajoute un fournisseur et importe sa liste de prix pour l'utiliser ici."
                    : 'Add a supplier and import its price list to use it here.'}
                </p>
                <Link
                  to="/suppliers"
                  onClick={close}
                  className="inline-flex items-center gap-2 py-2.5 px-4 rounded-xl font-semibold text-white text-sm"
                  style={{ backgroundColor: '#7A1F2B' }}
                  data-testid="picker-add-supplier-link"
                >
                  <Plus size={15} /> {lang === 'fr' ? 'Ajouter un fournisseur' : 'Add supplier'}
                </Link>
              </div>
            ) : !selectedId ? (
              <div className="overflow-y-auto flex-1 divide-y divide-[#F0E8D9]">
                {suppliers.map((s) => (
                  <button
                    key={s.id}
                    data-testid={`picker-supplier-${s.id}`}
                    onClick={() => setSelectedId(s.id)}
                    className="w-full text-left p-4 hover:bg-[#FAF7F2] transition-colors flex items-center gap-3"
                  >
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                      style={{ backgroundColor: '#FAF7F2', color: '#7A1F2B' }}
                    >
                      <Truck size={18} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-sm truncate" style={{ color: '#5A1620' }}>
                        {s.name}
                      </div>
                      {s.category && (
                        <div className="text-xs text-gray-500 truncate">{s.category}</div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <>
                {suppliers.length > 1 && (
                  <button
                    onClick={() => setSelectedId(null)}
                    className="px-4 py-2 text-xs font-semibold text-left border-b border-[#E5DCC9]"
                    style={{ color: '#7A1F2B' }}
                    data-testid="picker-back-to-suppliers"
                  >
                    ← {lang === 'fr' ? 'Changer de fournisseur' : 'Change supplier'}
                  </button>
                )}
                <div className="p-3 border-b border-[#E5DCC9] relative">
                  <Search
                    size={15}
                    className="absolute left-6 top-1/2 -translate-y-1/2"
                    style={{ color: '#9CA3AF' }}
                  />
                  <input
                    autoFocus
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={
                      lang === 'fr' ? 'Rechercher un prix…' : 'Search price…'
                    }
                    className="field !min-h-[44px] !pl-9"
                    data-testid="picker-search"
                  />
                </div>
                <div className="overflow-y-auto flex-1 divide-y divide-[#F0E8D9]">
                  {loadingPrices ? (
                    <div className="p-8 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#7A1F2B' }} />
                    </div>
                  ) : filtered.length === 0 ? (
                    <div className="p-6 text-center text-gray-500 text-xs">
                      {prices.length === 0
                        ? lang === 'fr'
                          ? 'Aucun prix importé pour ce fournisseur.'
                          : 'No prices imported for this supplier.'
                        : lang === 'fr'
                        ? 'Aucun résultat.'
                        : 'No results.'}
                    </div>
                  ) : (
                    filtered.map((p) => (
                      <button
                        key={p.id}
                        data-testid={`picker-price-${p.id}`}
                        onClick={() => pickPrice(p)}
                        className="w-full text-left p-4 hover:bg-[#FAF7F2] transition-colors flex items-center justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <div className="font-bold text-sm truncate" style={{ color: '#5A1620' }}>
                            {p.description}
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            {p.sku ? `${p.sku} · ` : ''}
                            {p.unit}
                          </div>
                        </div>
                        <div
                          className="font-heading font-black shrink-0"
                          style={{ color: '#7A1F2B' }}
                        >
                          {Number(p.unit_price).toFixed(2)} $
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
